import React from "react";
import _ from "lodash";
import moment from "moment";

import {useOtherAssetsData} from "../../CustomerDashboardV2/hooks/useOtherAssetsData";
import {ASSETS_LIABILITIES_ACTIONS, ASSETS_LIABILITIES_TYPES} from "../constants";
import {getNewCategoryToMove} from "../AssetForm/forms_schemes";
import useOtherAssetsLocalSyncOnUnmount from "./useOtherAssetsLocalSync";

const replaceLinksForAsset = (links, itemType, categoryId, itemId, newLinks) => {
  let leftFields = {id: 'asset_id', type: 'asset_type'};
  let rightField = {id: 'liability_id', type: 'liability_type'};

  if(itemType === ASSETS_LIABILITIES_TYPES.LIABILITIES){
    [leftFields, rightField] = [rightField, leftFields];
  }
  // Note: values might be int or str
  const linkItems = links.filter(link => !(link[leftFields.type] == categoryId && link[leftFields.id] == itemId));

  return _.concat(linkItems, (newLinks || []).map(link => ({
    [leftFields.id]: itemId,
    [leftFields.type]: categoryId,
    [rightField.id]: link.id,
    [rightField.type]: link.type_id
  })))
};

const assetsEqual = (a, b) => a.type_id === b.type_id && a.id === b.id;

function reducer(state, action) {
  const itemTypeData = _.get(state, ['data', action.itemType]);
  const categoryData = _.get(itemTypeData, action.categoryId);
  let item = action.item;
  let actionType = action.type;

  // check asset creation/expiry dates
  const targetDate = action.endDate || moment();
  if([ASSETS_LIABILITIES_ACTIONS.CREATE, ASSETS_LIABILITIES_ACTIONS.UPDATE].includes(actionType)) {
    const isFuture = item.creation_date_internal && moment(item.creation_date_internal).isAfter(targetDate);
    const isExpired = !!(item.expiration_date && moment(item.expiration_date).isBefore(targetDate));
    const isExpiredMoreThenYearAgo = isExpired && moment(item.expiration_date).isBefore(moment(targetDate).subtract(365, 'days'));

    if (isExpiredMoreThenYearAgo) {
      if(actionType === ASSETS_LIABILITIES_ACTIONS.CREATE){
        return state; // asset should not be added so nothing to do
      }

      actionType = ASSETS_LIABILITIES_ACTIONS.DELETE;
    } else {
      item = {
        ...item,
        is_expired: isExpired,
        is_future: isFuture,
      };

      // note: now we always get fresh asset_value from BE during create/edit
      // so no need re-store it when expired/future asset becomes active again
      if(isExpired || isFuture){
        item.asset_value = 0;
      }
    }
  }

  function _getUpdatedState(categoryAssets){
    let categoriesData = {...itemTypeData};
    // remove category if empty
    if (categoryAssets.length === 0) {
      delete categoriesData[action.categoryId];
    } else {
      categoriesData[action.categoryId] = {
        assets: categoryAssets
      }
    }

    return {
      ...state,
      data: {
        ...state.data,
        [action.itemType]: categoriesData,
      },
      updatedAt: +new Date(),
    }
  }

  switch (actionType) {
    case ASSETS_LIABILITIES_ACTIONS.RE_INIT: {
      return action.otherAssetsData
    }
    case ASSETS_LIABILITIES_ACTIONS.UPDATE: {
      const newCategoryData = getNewCategoryToMove(item, action.categoryId);
      if(newCategoryData){
        // move into another group
        let newState = reducer(state, {...action, type: ASSETS_LIABILITIES_ACTIONS.CREATE, ...newCategoryData})
        newState = reducer(newState, {...action, type: ASSETS_LIABILITIES_ACTIONS.DELETE})
        return newState;
      }

      const newState = _getUpdatedState(categoryData.assets.map(a => assetsEqual(a, item) ? item : a));
      newState.data.asset_liability_links = replaceLinksForAsset(
        newState.data.asset_liability_links, action.itemType, item.type_id, item.id, item.links
      );
      return newState;
    }
    case ASSETS_LIABILITIES_ACTIONS.CREATE: {
      const newState = _getUpdatedState([..._.get(categoryData, 'assets', []), item]);
      newState.data.asset_liability_links = replaceLinksForAsset(
        newState.data.asset_liability_links, action.itemType, item.type_id, item.id, item.links
      );
      return newState;
    }
    case ASSETS_LIABILITIES_ACTIONS.BULK_CREATE: {
      let categoriesData = {...itemTypeData};
      // here items is a dict of assets/liabilities
      Object.entries(item).forEach(([category, newItems]) => {
        categoriesData[category] = {
          assets: [..._.get(categoriesData[category], 'assets', []), ...newItems]
        }
      });

      return {
        ...state,
        data: {
          ...state.data,
          [action.itemType]: categoriesData,
      },
      updatedAt: +new Date(),
      }
    }
    case ASSETS_LIABILITIES_ACTIONS.DELETE: {
      const newState = _getUpdatedState(categoryData.assets.filter(a => !assetsEqual(a, item)));
      newState.data.asset_liability_links = replaceLinksForAsset(
        newState.data.asset_liability_links, action.itemType, item.type_id, item.id, []
      );
      return newState;
    }
    default: {
      return state;
    }
  }
}

const useOtherAssetsAndLiabilitiesData = (customerId, startDate, endDate, withOtherAssets, withLiabilities, cancelOnUnmount) => {
  const [otherAssetsData, fetch, updateSharedData] = useOtherAssetsData(
    customerId, [], startDate, endDate, withOtherAssets, withLiabilities, false, false, undefined, true, cancelOnUnmount
  );
  const [data, dispatch] = React.useReducer(reducer, otherAssetsData);

  useOtherAssetsLocalSyncOnUnmount(otherAssetsData, data, updateSharedData);

  React.useEffect(() => {
    if(!otherAssetsData.loading){
      dispatch({type: ASSETS_LIABILITIES_ACTIONS.RE_INIT, otherAssetsData})
    }
  }, [otherAssetsData.updatedAt]);

  return [data, fetch, dispatch]
};

export default useOtherAssetsAndLiabilitiesData;